import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Chip, 
  Box, 
  Avatar, 
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaEdit, FaTrash, FaCloudUploadAlt, FaCloudDownloadAlt, FaEye, FaEyeSlash, FaSearch } from 'react-icons/fa';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, RolesGrid, RoleCard, RoleCardContent, RoleDetailsGrid, ItalicText, RoleButtons } from './styles';
import EmptyState from '../../common/EmptyState';
import LoadingSpinner from '../../common/LoadingSpinner';
import ConfirmationModal from '../../common/ConfirmationModal';
import { COLORS } from '../../../constants/radar';
import { getRoleIcon } from './utils/iconMap';
import { useAlert } from '../../../contexts/AlertContext';
import { permissionsApi } from '../../../services/api/permissionsApi';
import { toLocalDate } from '../../../utils/dateOnly';

// Helper function to format grades display
const formatGradesDisplay = (grades) => {
  if (!grades || grades.length === 0) {
    return 'All levels';
  }

  // Sort grades by order
  const sortedGrades = [...grades].sort((a, b) => (a.order || 0) - (b.order || 0));

  // Find consecutive sequences
  const findConsecutiveSequences = () => {
    const sequences = [];
    let currentSeq = [sortedGrades[0]];

    for (let i = 1; i < sortedGrades.length; i++) {
      const prevOrder = sortedGrades[i - 1].order;
      const currOrder = sortedGrades[i].order;

      if (prevOrder !== undefined && currOrder !== undefined && currOrder === prevOrder + 1) {
        currentSeq.push(sortedGrades[i]);
      } else {
        sequences.push(currentSeq);
        currentSeq = [sortedGrades[i]];
      }
    }
    sequences.push(currentSeq);
    return sequences;
  };

  const sequences = findConsecutiveSequences();

  // Find if there's a sequence with 3+ consecutive grades
  const longSequence = sequences.find(seq => seq.length >= 3);

  if (longSequence) {
    // Get grades not in the long sequence
    const longSeqIds = new Set(longSequence.map(g => g.id));
    const others = sortedGrades.filter(g => !longSeqIds.has(g.id));

    const rangeStr = `${longSequence[0].label}–${longSequence[longSequence.length - 1].label}`;

    if (others.length === 0) {
      return rangeStr;
    } else if (others.length === 1) {
      return `${others[0].label}, ${rangeStr}`;
    } else {
      return `${others[0].label} (+${others.length - 1}), ${rangeStr}`;
    }
  }

  // No long consecutive sequence - show normally
  if (sortedGrades.length > 3) {
    const firstThree = sortedGrades.slice(0, 3).map(g => g.label).join(', ');
    return `${firstThree} (+${sortedGrades.length - 3})`;
  }

  // Join with comma
  return sortedGrades.map(g => g.label).join(', ');
};

const Roles = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [searchTerm, setSearchTerm] = useState('');
  const [rolesData, setRolesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    role: null,
    deleting: false
  });

  // Load roles data on component mount
  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const roles = await permissionsApi.getAllRoles();
      setRolesData(roles);
    } catch (error) {
      console.error('Error loading roles:', error);
      showAlert(error.message || 'Failed to load roles', 'error');
      setRolesData([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = rolesData.filter(role => 
    role.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleRoleClick = (role) => {
    navigate(`/permissions/roles/${role.id}`);
  };

  const handlePublishToggle = async (role) => {
    try {
      const updatedRole = role.isPublished 
        ? await permissionsApi.unpublishRole(role.id)
        : await permissionsApi.publishRole(role.id);
      
      setRolesData(prevRoles => 
        prevRoles.map(r => 
          r.id === role.id ? { ...r, ...updatedRole, permissionsCount: r.permissionsCount, employeesCount: r.employeesCount } : r
        )
      );
      
      showAlert(
        `Role ${role.isPublished ? 'unpublished' : 'published'} successfully`, 
        'success'
      );
    } catch (error) {
      console.error('Error toggling role publish status:', error);
      showAlert(error.message || `Failed to ${role.isPublished ? 'unpublish' : 'publish'} role`, 'error');
    }
  };

  const getStatusColor = (isPublished) => {
    return isPublished ? '#E6F4EA' : '#F3F4F6';
  };

  const getStatusTextColor = (isPublished) => {
    return isPublished ? '#1E8E3E' : '#6B7280';
  };

  const getStatusText = (isPublished) => {
    return isPublished ? 'Published' : 'Draft';
  };



  const handleCreateRole = () => {
    navigate('/permissions/roles/new');
  };

  const handleDeleteClick = (role) => {
    setDeleteModal({
      open: true,
      role,
      deleting: false
    });
  };

  const handleDelete = async () => {
    if (!deleteModal.role) return;
    
    try {
      setDeleteModal(prev => ({ ...prev, deleting: true }));
      
      await permissionsApi.deleteRole(deleteModal.role.id);
      
      // Remove role from the list
      setRolesData(prev => prev.filter(role => role.id !== deleteModal.role.id));
      
      showAlert('Role deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting role:', error);
      showAlert(error.message || 'Failed to delete role', 'error');
    } finally {
      // Close the modal
      setDeleteModal({ open: false, role: null, deleting: false });
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/permissions')}>
            <FaArrowLeft /> Back to Permissions
          </BackButton>
          <Title>Roles</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton onClick={() => setShowControls(!showControls)}>
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <TextField
              placeholder="Search Roles"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                flex: 1,
                maxWidth: '500px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  height: '40px',
                  fontSize: '14px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  }
                },
                '& .MuiInputBase-input': {
                  padding: '8px 14px',
                }
              }}
            />

            <HeaderButton 
              onClick={handleCreateRole}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Create Role
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer show={showControls}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2
          }}>
            <Typography 
              sx={{ 
                color: '#64748B',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }}>
                {loading ? '...' : filteredRoles.length}
              </Box>
              {filteredRoles.length === 1 ? 'role' : 'roles'} found
            </Typography>
          </Box>

          {loading ? (
            <LoadingSpinner message="Loading roles..." />
          ) : filteredRoles.length === 0 ? (
            <EmptyState
              title="No roles found"
              description={searchTerm ? 
                "No roles match your search criteria. Try adjusting your search term." : 
                "No roles have been created yet. Click the 'Create Role' button to create your first role."}
            />
          ) : (
            <RolesGrid>
              {filteredRoles.map((role) => (
                <RoleCard 
                  key={role.id || role.name}
                >
                  <RoleCardContent>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      mb: 2,
                      gap: 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: role.color || '#2196f3', 
                            width: 48, 
                            height: 48 
                          }}
                        >
                          {getRoleIcon(role.icon)}
                        </Avatar>
                        <Box>
                          <Typography
                            sx={{ 
                              color: COLORS.PRIMARY,
                              fontSize: '18px',
                              fontWeight: 600,
                              marginBottom: '4px'
                            }}
                          >
                            {role.displayName}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={role.isSystemRole ? 'System' : 'Custom'}
                              size="small"
                              sx={{ 
                                backgroundColor: role.isSystemRole ? '#E3F2FD' : '#FFF3E0',
                                color: role.isSystemRole ? '#1976D2' : '#F57C00',
                                fontWeight: 500,
                                fontSize: '12px'
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              Level {role.hierarchyLevel}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Chip
                        label={getStatusText(role.isPublished)}
                        size="small"
                        sx={{ 
                          backgroundColor: getStatusColor(role.isPublished),
                          color: getStatusTextColor(role.isPublished),
                          border: '1px solid',
                          borderColor: `${getStatusTextColor(role.isPublished)}20`,
                          height: '24px',
                          minWidth: 'fit-content',
                          '& .MuiChip-label': {
                            fontSize: '13px',
                            padding: '0 8px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                    </Box>
                    
                    <RoleDetailsGrid>
                      {role.description ? (
                        <Typography color="#333">
                          {role.description}
                        </Typography>
                      ) : (
                        <ItalicText>No description provided</ItalicText>
                      )}

                      {/* Show positions for non-system roles */}
                      {!role.isSystemRole && (
                        <Box sx={{ mt: 2 }}>
                          {role.rolePositionMappings && role.rolePositionMappings.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {role.rolePositionMappings.map((mapping) => (
                                <Chip
                                  key={mapping.id}
                                  label={
                                    mapping.requiresGrade ? (
                                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box component="span" sx={{ fontWeight: 500 }}>
                                          {mapping.label}
                                        </Box>
                                        <Box component="span" sx={{ color: '#78909C', fontSize: '11px', fontWeight: 400 }}>
                                          · {formatGradesDisplay(mapping.grades)}
                                        </Box>
                                      </Box>
                                    ) : mapping.label
                                  }
                                  size="small"
                                  sx={{
                                    backgroundColor: '#E3F2FD',
                                    color: '#1565C0',
                                    border: '1px solid #90CAF9',
                                    fontWeight: 500,
                                    fontSize: '13px',
                                    height: '26px',
                                    '& .MuiChip-label': {
                                      px: 1.5
                                    }
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Box component="span" sx={{ fontStyle: 'italic', color: '#94A3B8', fontSize: '13px' }}>
                              No positions attached
                            </Box>
                          )}
                        </Box>
                      )}

                      {role.publishedAt && role.isPublished && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Published: {toLocalDate(role.publishedAt)?.toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Permissions Count */}
                      <Box sx={{
                        marginTop: '16px',
                        paddingTop: '12px',
                        borderTop: '1px solid #E2E8F0',
                        fontSize: '13px',
                        color: '#475569'
                      }}>
                        {role.permissionsCount > 0 ? (
                          <Box component="span" sx={{ fontWeight: '500' }}>
                            <Box component="span" sx={{ fontWeight: '700', fontSize: '15px' }}>
                              {role.permissionsCount}
                            </Box> permission{role.permissionsCount === 1 ? '' : 's'} defined
                          </Box>
                        ) : (
                          <Box component="span" sx={{ fontStyle: 'italic', color: '#94A3B8' }}>
                            No permissions defined
                          </Box>
                        )}
                      </Box>
                    </RoleDetailsGrid>

                    <RoleButtons>
                      {role.isPublished ? (
                        <IconButton
                          onClick={() => handlePublishToggle(role)}
                          color="primary"
                          size="small"
                          title="Unpublish role"
                        >
                          <FaCloudDownloadAlt />
                        </IconButton>
                      ) : (
                        <IconButton
                          onClick={() => handlePublishToggle(role)}
                          color="primary"
                          size="small"
                          title="Publish role"
                        >
                          <FaCloudUploadAlt />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={() => handleRoleClick(role)}
                        color="primary"
                        size="small"
                        title="Edit role"
                      >
                        <FaEdit />
                      </IconButton>
                      {!role.isSystemRole && (
                        <IconButton
                          onClick={() => handleDeleteClick(role)}
                          color="error"
                          size="small"
                          title="Delete role"
                        >
                          <FaTrash />
                        </IconButton>
                      )}
                    </RoleButtons>
                  </RoleCardContent>
                </RoleCard>
              ))}
            </RolesGrid>
          )}
        </MainContainer>
      </Content>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, role: null, deleting: false })}
        onConfirm={handleDelete}
        title="Delete Role"
        message={`Are you sure you want to delete the "${deleteModal.role?.displayName}" role? This action cannot be undone.`}
        loading={deleteModal.deleting}
      />
    </Container>
  );
};

export default Roles;